/* 
 *  This file main.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2022  
 *  (C) 2015-2022. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *  
 */
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { enableProdMode, ErrorHandler, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_BASE_HREF, DatePipe } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { OAuthModule } from 'angular-oauth2-oidc';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { NewRelicHttpInterceptor } from './app/global/services/newrelic-handler/newrelic-http.interceptor';
import { routes } from './app/app-routing.module';
import { getBaseLocationForBaseHref } from './app/global/util/basehref.util';
import { AuthGuardService } from './app/global/guard/auth-guard-service';
import { AuthService } from './app/global/services/auth-service';
import { HttpService } from './app/global/services/http-service';
import { SharedService } from './app/global/services/shared.service';
import { SnackbarAlertService } from './app/global/services/snackbar-alert.service';
import { ClientErrorHandler } from './app/global/services/error-handler/client-error-handler.service';
import { LogErrorsService } from './app/global/services/error-handler/log-errors';
import { ProgressButtonService } from './app/global/services/progress-button.service';
import { PageLoaderService } from './app/global/services/page-loader.service';
import { FileUploadService } from './app/global/services/file-upload.service';
import { ConfigurationService } from './app/global/services/configuration.service';
import { LoadScriptService } from './app/global/services/load-script.service';
import { SuperUserResolverService } from './app/global/services/super-user.resolver.service';
import { SuperUserServerErrorHandlerService } from './app/global/services/error-handler/super-user-server-error-handler.service';

if (environment.production) {
  enableProdMode();
   window.console.log = function () { };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    importProvidersFrom(OAuthModule.forRoot()),
    AuthGuardService,
    AuthService,
    HttpService,
    SharedService,
    SnackbarAlertService,
    ProgressButtonService,
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseLocationForBaseHref
    },
    {
      provide: ErrorHandler,
      useClass: ClientErrorHandler
    },
    LogErrorsService,
    PageLoaderService,
    ConfirmationService,
    FileUploadService,
    ConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: (ds: ConfigurationService) => function () {
        return ds.loadConfig();
      },
      deps: [ConfigurationService],
      multi: true
    },
    LoadScriptService,
    SuperUserResolverService,
    Title,
    DatePipe,
    SuperUserServerErrorHandlerService,
    provideHttpClient(withInterceptorsFromDi()),
    // NEW RELIC: Register HTTP Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NewRelicHttpInterceptor,
      multi: true
    }
  ]
}).then(() => {
  if (environment.newRelic.enabled) {
    // Use setTimeout to allow Zone.js to fully settle
    setTimeout(() => {
      try {
        // Capture native console functions before agent patches
        const nativeWarn = console.warn.bind(console);
        const nativeError = console.error.bind(console);

         new BrowserAgent({
           init: {
             distributed_tracing: { enabled: true },
             privacy: { cookies_enabled: true },
             ajax: { deny_list: [], enabled: true },
             session_trace: { enabled: true },
             session_replay: { enabled: true },
             jserrors: { 
               enabled: true, 
               harvestConsoleErrors: false 
             } as any,
             logging: { 
               enabled: true, 
               harvestConsoleErrors: false,
               harvestConsoleWarns: false,
               harvestConsoleInfo: false
             } as any,
             metrics: { enabled: true },
             page_action: { enabled: true }
           },
           info: {
             beacon: 'bam.nr-data.net',
             errorBeacon: 'bam.nr-data.net',
             licenseKey: environment.newRelic.licenseKey,
             applicationID: environment.newRelic.applicationID,
             sa: 1
           },
           loader_config: {
             accountID: environment.newRelic.accountID,
             trustKey: environment.newRelic.trustKey,
             agentID: environment.newRelic.agentID,
             licenseKey: environment.newRelic.licenseKey,
             applicationID: environment.newRelic.applicationID
           }
         });
        console.warn = nativeWarn;
        console.error = nativeError;

        const nr: any = (window as any).newrelic;
        
        if (nr?.log) {
          const originalLog = nr.log.bind(nr);
          nr.log = function(message: string, attributes?: any) {
            const enhancedAttributes = {
              ...attributes
            };
            return originalLog(message, enhancedAttributes);
          };
        }
      } catch (error) {
        console.error('New Relic initialization failed:', error);
      }
    }, 500);
  }
}).catch(err => console.log(err));
